import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { 
  ArrowLeft,
  Menu, 
  BookOpen, 
  CheckCircle2, 
  Circle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  AlertTriangle,
  Video,
  FileText,
  PanelLeftClose,
  PanelLeft,
  Users,
  MessageSquare,
  Trophy
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Capacitor } from "@capacitor/core";

interface Module {
  id: string;
  title: string;
  description?: string;
  order_index: number;
  sections?: {
    id: string;
    title: string;
    description?: string;
    image_url?: string;
    activities?: {
      id: string;
      title: string;
      activity_type: string;
      duration_minutes?: number;
      content?: any;
      description?: string;
      is_required?: boolean;
    }[];
  }[];
  moduleActivities?: {
    id: string;
    title: string;
    activity_type: string;
    duration_minutes?: number;
    content?: any;
    description?: string;
    is_required?: boolean;
  }[];
}

interface CourseLearningLayoutProps {
  children: ReactNode;
  courseTitle: string;
  courseId: string;
  modules: Module[];
  currentModuleIndex: number;
  currentSubPageIndex: number;
  completedModules: Set<string>;
  completionPercentage: number;
  onModuleSelect: (index: number) => void;
  onSubPageSelect: (moduleIndex: number, subPageIndex: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  hasUnsavedProgress?: boolean;
  isLastSubPage?: boolean;
  isFirstSubPage?: boolean;
  isLastPage?: boolean;
  nextDisabled?: boolean;
  isPreview?: boolean;
  onExitPreview?: () => void;
}

export function CourseLearningLayout({
  children,
  courseTitle,
  courseId,
  modules,
  currentModuleIndex,
  currentSubPageIndex,
  completedModules,
  completionPercentage,
  onModuleSelect,
  onSubPageSelect,
  onPrevious,
  onNext,
  hasUnsavedProgress = false,
  isLastSubPage = false,
  isFirstSubPage = true,
  isLastPage = false,
  nextDisabled = false,
  isPreview = false,
  onExitPreview
}: CourseLearningLayoutProps) {
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();
  const exitPath = isNative ? "/app/courses" : "/learning/courses";
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<'path' | 'learners' | 'discuss'>('path');

  const handleExitCourse = () => {
    if (isPreview && onExitPreview) {
      onExitPreview();
      return;
    }
    if (hasUnsavedProgress) {
      setShowExitDialog(true);
    } else {
      navigate(exitPath);
    }
  };

  const confirmExit = () => {
    setShowExitDialog(false);
    navigate(exitPath);
  };

  const handlePrevious = () => {
    onPrevious();
  };

  const handleNext = () => {
    onNext();
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar - Brand Theme */}
      <aside 
        className={cn(
          "hidden lg:flex flex-col bg-primary text-primary-foreground transition-all duration-300 relative",
          sidebarCollapsed ? "w-0 overflow-hidden" : "w-[420px] xl:w-[480px]"
        )}
      >
        {/* Back to course link */}
        <div className="px-4 py-3 border-b border-white/20">
          <button
            onClick={handleExitCourse}
            className="flex items-center gap-2 text-sm text-white/90 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {isPreview ? 'Exit Preview' : 'Back to course page'}
          </button>
          {isPreview && (
            <Badge variant="secondary" className="mt-2 bg-white/20 text-white border-white/30 text-xs">
              Preview Mode
            </Badge>
          )}
        </div>

        {/* Course Title & Progress */}
        <div className="px-4 py-4 border-b border-white/20">
          <h1 className="text-lg font-semibold text-white leading-tight mb-3">
            {courseTitle}
          </h1>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-white/30 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            <span className="text-xs text-white/80 font-medium whitespace-nowrap">
              {completionPercentage}%
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/20">
          <button
            onClick={() => setActiveTab('path')}
            className={cn(
              "flex-1 py-3 text-sm font-medium transition-colors relative",
              activeTab === 'path' 
                ? "text-white" 
                : "text-white/70 hover:text-white/90"
            )}
          >
            Path
            {activeTab === 'path' && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-white rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('learners')}
            className={cn(
              "flex-1 py-3 text-sm font-medium transition-colors relative",
              activeTab === 'learners' 
                ? "text-white" 
                : "text-white/70 hover:text-white/90"
            )}
          >
            Learners
            {activeTab === 'learners' && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-white rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('discuss')}
            className={cn(
              "flex-1 py-3 text-sm font-medium transition-colors relative",
              activeTab === 'discuss' 
                ? "text-white" 
                : "text-white/70 hover:text-white/90"
            )}
          >
            Discuss
            {activeTab === 'discuss' && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-white rounded-full" />
            )}
          </button>
        </div>

        {/* Tab Content */}
        <ScrollArea className="flex-1">
          {activeTab === 'path' && (
            <CourseOutlineEnhanced 
              modules={modules}
              currentModuleIndex={currentModuleIndex}
              currentSubPageIndex={currentSubPageIndex}
              completedModules={completedModules}
              onSubPageSelect={onSubPageSelect}
            />
          )}
          {activeTab === 'learners' && (
            <div className="p-6 text-white/70 text-sm text-center">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-white/60" />
              </div>
              <span className="font-medium text-white block mb-1">Learners</span>
              <p className="text-white/50 text-xs">Feature locked</p>
            </div>
          )}
          {activeTab === 'discuss' && (
            <div className="p-6 text-white/70 text-sm text-center">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="h-6 w-6 text-white/60" />
              </div>
              <span className="font-medium text-white block mb-1">Discussion</span>
              <p className="text-white/50 text-xs">Feature locked</p>
            </div>
          )}
        </ScrollArea>

        {/* Collapse Button */}
        <button
          onClick={() => setSidebarCollapsed(true)}
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-12 bg-primary rounded-r-md flex items-center justify-center text-primary-foreground/80 hover:text-primary-foreground transition-colors shadow-md"
          title="Collapse sidebar"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </aside>

      {/* Expand Button (when collapsed) */}
      {sidebarCollapsed && (
        <button
          onClick={() => setSidebarCollapsed(false)}
          className="hidden lg:flex fixed left-0 top-1/2 -translate-y-1/2 w-6 h-12 bg-primary rounded-r-md items-center justify-center text-primary-foreground/80 hover:text-primary-foreground transition-colors shadow-md z-40"
          title="Expand sidebar"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navigation Bar */}
        <header className="h-12 border-b bg-card flex items-center justify-between px-4 shrink-0">
          {/* Mobile Menu */}
          <div className="lg:hidden">
            {isNative ? (
              <Button variant="ghost" size="icon" onClick={handleExitCourse} aria-label="Back to courses">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            ) : (
              <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-0 bg-primary border-none">
                  <SheetHeader className="p-4 border-b border-white/20">
                    <SheetTitle className="text-left text-white">{courseTitle}</SheetTitle>
                  </SheetHeader>
                  <div className="px-4 py-3 border-b border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-white/30 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-white rounded-full"
                          style={{ width: `${completionPercentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-white/80 font-medium">{completionPercentage}%</span>
                    </div>
                  </div>
                  <ScrollArea className="h-[calc(100vh-140px)]">
                    <CourseOutlineEnhanced 
                      modules={modules}
                      currentModuleIndex={currentModuleIndex}
                      currentSubPageIndex={currentSubPageIndex}
                      completedModules={completedModules}
                      onSubPageSelect={(moduleIndex, subPageIndex) => {
                        onSubPageSelect(moduleIndex, subPageIndex);
                        setSidebarOpen(false);
                      }}
                    />
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            )}
          </div>

          {/* Previous Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={isFirstSubPage && currentModuleIndex === 0}
            className="text-xs sm:text-sm"
          >
            <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
            <span className="hidden sm:inline">Previous</span>
          </Button>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center">
            <div className={cn(
              "w-7 h-7 rounded-full border-2 flex items-center justify-center",
              completionPercentage === 100 
                ? "border-primary bg-primary" 
                : "border-primary"
            )}>
              {completionPercentage === 100 ? (
                <CheckCircle2 className="h-4 w-4 text-primary-foreground" />
              ) : (
                <div className="w-2.5 h-2.5 rounded-full bg-primary" />
              )}
            </div>
          </div>

          {/* Next / Complete Button */}
          {isLastPage ? (
            <Button
              size="sm"
              onClick={handleNext}
              disabled={nextDisabled}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs sm:text-sm"
            >
              <Trophy className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
              <span className="hidden sm:inline">Complete</span>
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleNext}
              disabled={nextDisabled}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs sm:text-sm"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1" />
            </Button>
          )}
        </header>

        {/* Course Content */}
        <main className="flex-1 overflow-auto bg-background">
          <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Exit Course?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Are you sure you want to exit this course?</p>
                <p className="text-amber-600 dark:text-amber-400 font-medium">
                  Your progress on the current page may not be saved if you haven't completed it.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Learning</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmExit}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Exit Course
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Enhanced Course Outline with Collapsible Modules
function CourseOutlineEnhanced({
  modules,
  currentModuleIndex,
  currentSubPageIndex,
  completedModules,
  onSubPageSelect
}: {
  modules: Module[];
  currentModuleIndex: number;
  currentSubPageIndex: number;
  completedModules: Set<string>;
  onSubPageSelect: (moduleIndex: number, subPageIndex: number) => void;
}) {
  const [expandedModules, setExpandedModules] = useState<Set<number>>(
    new Set([currentModuleIndex])
  );

  const toggleModule = (index: number) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedModules(newExpanded);
  };

  // Calculate module duration from activities
  const getModuleDuration = (module: Module): number => {
    let duration = 0;
    module.sections?.forEach(section => {
      section.activities?.forEach(activity => {
        duration += activity.duration_minutes || 0;
      });
    });
    module.moduleActivities?.forEach(activity => {
      duration += activity.duration_minutes || 0;
    });
    return duration;
  };

  return (
    <div className="py-2">
      {modules.map((module, index) => {
        const isCompleted = completedModules.has(module.id);
        const isCurrent = index === currentModuleIndex;
        const isExpanded = expandedModules.has(index);
        const duration = getModuleDuration(module);
        const hasSections = module.sections && module.sections.length > 0;
        const hasModuleActivities = module.moduleActivities && module.moduleActivities.length > 0;

        return (
          <div key={module.id} className="border-b border-white/10 last:border-b-0">
            <Collapsible open={isExpanded} onOpenChange={() => toggleModule(index)}>
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-white/5 transition-colors",
                    isCurrent && "bg-white/10"
                  )}
                >
                  {/* Module Status & Duration */}
                  <div className="flex items-center gap-2 shrink-0 mt-0.5">
                    <Badge 
                      variant="secondary" 
                      className={cn(
                        "text-[10px] px-1.5 py-0 h-5 font-normal",
                        isCompleted 
                          ? "bg-white/30 text-white" 
                          : "bg-white/20 text-white/80"
                      )}
                    >
                      {duration > 0 ? `${duration}m` : 'MIN'}
                    </Badge>
                  </div>

                  {/* Module Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "text-sm font-medium",
                        isCurrent ? "text-white" : "text-white/90"
                      )}>
                        {index + 1}. {module.title}
                      </span>
                      {isCompleted && (
                        <CheckCircle2 className="h-4 w-4 text-white/80 shrink-0" />
                      )}
                    </div>
                  </div>

                  {/* Expand Icon */}
                  <ChevronDown className={cn(
                    "h-4 w-4 text-white/60 shrink-0 transition-transform mt-0.5",
                    isExpanded && "rotate-180"
                  )} />
                </button>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="pb-2">
                  {/* Module Overview - always show as first item (subPageIndex = 0) */}
                  <button
                    onClick={() => onSubPageSelect(index, 0)}
                    className={cn(
                      "w-full text-left pl-10 pr-4 py-2 flex items-center gap-3 hover:bg-white/5 transition-colors group",
                      isCurrent && currentSubPageIndex === 0 && "bg-white/5"
                    )}
                  >
                    <div className="shrink-0">
                      {isCompleted ? (
                        <CheckCircle2 className="h-4 w-4 text-white/70" />
                      ) : isCurrent && currentSubPageIndex === 0 ? (
                        <div className="w-4 h-4 rounded-full border-2 border-white/70" />
                      ) : (
                        <Circle className="h-4 w-4 text-white/40" />
                      )}
                    </div>
                    <BookOpen className="h-4 w-4 text-white/60 shrink-0" />
                    <span className={cn(
                      "text-sm group-hover:text-white",
                      isCurrent && currentSubPageIndex === 0 ? "text-white font-medium" : "text-white/80"
                    )}>
                      Module Overview
                    </span>
                  </button>

                  {/* Sections (subPageIndex = 1, 2, 3, ...) */}
                  {module.sections?.map((section, sectionIndex) => {
                    const subPageIndex = sectionIndex + 1; // +1 because 0 is overview
                    const isCurrentSection = isCurrent && currentSubPageIndex === subPageIndex;
                    
                    return (
                      <button
                        key={section.id}
                        onClick={() => onSubPageSelect(index, subPageIndex)}
                        className={cn(
                          "w-full text-left pl-10 pr-4 py-2 flex items-center gap-3 hover:bg-white/5 transition-colors group",
                          isCurrentSection && "bg-white/5"
                        )}
                      >
                        <div className="shrink-0">
                          {isCompleted ? (
                            <CheckCircle2 className="h-4 w-4 text-white/70" />
                          ) : isCurrentSection ? (
                            <div className="w-4 h-4 rounded-full border-2 border-white/70" />
                          ) : (
                            <Circle className="h-4 w-4 text-white/40" />
                          )}
                        </div>
                        <FileText className="h-4 w-4 text-white/60 shrink-0" />
                        <span className={cn(
                          "text-sm group-hover:text-white truncate flex-1",
                          isCurrentSection ? "text-white font-medium" : "text-white/80"
                        )}>
                          {section.title}
                        </span>
                      </button>
                    );
                  })}

                  {/* Module-level activities (after sections) */}
                  {module.moduleActivities && module.moduleActivities.length > 0 && (
                    <>
                      {module.moduleActivities.map((activity, activityIndex) => {
                        const subPageIndex = (module.sections?.length || 0) + 1 + activityIndex;
                        const isCurrentActivity = isCurrent && currentSubPageIndex === subPageIndex;
                        
                        return (
                          <button
                            key={activity.id}
                            onClick={() => onSubPageSelect(index, subPageIndex)}
                            className={cn(
                              "w-full text-left pl-10 pr-4 py-2 flex items-center gap-3 hover:bg-white/5 transition-colors group",
                              isCurrentActivity && "bg-white/5"
                            )}
                          >
                            <div className="shrink-0">
                              {isCompleted ? (
                                <CheckCircle2 className="h-4 w-4 text-white/70" />
                              ) : isCurrentActivity ? (
                                <div className="w-4 h-4 rounded-full border-2 border-white/70" />
                              ) : (
                                <Circle className="h-4 w-4 text-white/40" />
                              )}
                            </div>
                            {activity.activity_type === 'video' ? (
                              <Video className="h-4 w-4 text-white/60 shrink-0" />
                            ) : (
                              <FileText className="h-4 w-4 text-white/60 shrink-0" />
                            )}
                            <span className={cn(
                              "text-sm group-hover:text-white truncate flex-1",
                              isCurrentActivity ? "text-white font-medium" : "text-white/80"
                            )}>
                              {activity.title}
                            </span>
                            {activity.duration_minutes && activity.duration_minutes > 0 && (
                              <span className="text-xs text-white/50 shrink-0">
                                {String(Math.floor(activity.duration_minutes / 60)).padStart(2, '0')}:
                                {String(activity.duration_minutes % 60).padStart(2, '0')}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        );
      })}
    </div>
  );
}
